import { buildSkillLinkUrl, MAX_PINNED_TURN_SKILLS } from '@aline/common';
import { ElementApi } from 'platejs';
import type { Descendant, SlateEditor, TElement, TText } from 'platejs';
import type { Apollo } from 'src/gql/generated';

import { ELEMENT_SKILL } from '../../AlineMarkdownEditor/plugins/skill-element-plugin';

export interface TSkillElement extends TElement {
  children: TText[];
  /** When the skill applies. The chip's only preview of what it will do. */
  skillDescription: string;
  skillId: string;
  /** The skill's spec name, e.g. `nda-review`, used when the message is copied as text. */
  skillSlug: string;
  skillVersionId: string;
  type: typeof ELEMENT_SKILL;
  /** Display label, i.e. the skill's name. */
  value: string;
  /** Set when the chip pins an unpublished test version of the author's draft. */
  skillIsTestVersion?: boolean;
}

export type PinnedSkill = {
  description: string;
  name: string;
  skillId: string;
  versionId: string;
  isTestVersion?: boolean;
};

/** Why a pin was refused, so the caller can say something useful rather than do nothing. */
export type SkillPinRefusal = 'duplicate' | 'limitReached';

export const SKILL_PIN_REFUSAL_MESSAGES = {
  duplicate: 'This skill is already in your message.',
  limitReached: `You can add up to ${MAX_PINNED_TURN_SKILLS} skills to a message.`,
} satisfies Record<SkillPinRefusal, string>;

/** Whether a picker that offers `offeredScopes` lists a skill of `scope`; null offers every scope. */
export function getIsSkillScopeOffered(
  scope: Apollo.AgentSkillScope,
  offeredScopes: readonly Apollo.AgentSkillScope[] | null
) {
  return offeredScopes === null || offeredScopes.includes(scope);
}

export function getIsSkillElement(node: unknown): node is TSkillElement {
  if (!ElementApi.isElement(node) || node.type !== ELEMENT_SKILL) return false;
  return (
    typeof node.skillId === 'string' &&
    typeof node.skillVersionId === 'string' &&
    typeof node.skillSlug === 'string' &&
    typeof node.skillDescription === 'string' &&
    typeof node.value === 'string'
  );
}

export function buildSkillElement({
  description,
  isTestVersion,
  name,
  skillId,
  versionId,
}: PinnedSkill): TSkillElement {
  return {
    children: [{ text: '' }],
    skillDescription: description,
    skillId,
    ...(isTestVersion && { skillIsTestVersion: true }),
    skillSlug: name,
    skillVersionId: versionId,
    type: ELEMENT_SKILL,
    value: name,
  };
}

/** Markdown for a chip, as the composer serializes it. */
export function buildSkillMarkdownLink(skill: Pick<PinnedSkill, 'name' | 'skillId' | 'versionId'>) {
  return `[${skill.name}](${buildSkillLinkUrl(skill)})`;
}

/**
 * Read the Skills pinned to the current message in document order.
 *
 * Walks the editor value rather than parsing the serialized markdown. A chip serializes to
 * an inline `[name](skill:id/version)` link, which keeps its place in the prompt, but the
 * pinned ids are sent to the server separately, and those come from the chips themselves.
 */
export function getPinnedSkillsFromEditor(editor: SlateEditor): PinnedSkill[] {
  return Array.from(editor.api.nodes({ at: [], match: { type: ELEMENT_SKILL } })).flatMap(
    ([node]) => (getIsSkillElement(node) ? [getPinnedSkillFromElement(node)] : [])
  );
}

/**
 * Why `skillId` cannot join the skills already `pinned`, or null when it can.
 *
 * Both refusals mirror a rule `sendAgentPrompt` enforces: it rejects a turn that repeats a
 * skill, and one that pins more than `MAX_PINNED_TURN_SKILLS`.
 */
export function getSkillPinRefusal({
  pinned,
  skillId,
}: {
  pinned: PinnedSkill[];
  skillId: string;
}): SkillPinRefusal | null {
  if (pinned.some(candidate => candidate.skillId === skillId)) return 'duplicate';
  if (pinned.length >= MAX_PINNED_TURN_SKILLS) return 'limitReached';
  return null;
}

/**
 * Pin a skill chip at the selection, without disturbing existing ones.
 *
 * Skills are inline void nodes, like file mentions, so each selection keeps its position.
 * Refusing here, rather than at send, keeps the composer from holding a turn the server
 * would reject.
 */
export function insertSkillElement(
  editor: SlateEditor,
  skill: PinnedSkill
): 'inserted' | SkillPinRefusal {
  const refusal = getSkillPinRefusal({
    pinned: getPinnedSkillsFromEditor(editor),
    skillId: skill.skillId,
  });
  if (refusal) return refusal;
  editor.tf.insertNodes([buildSkillElement(skill), { text: ' ' }], { select: true });
  return 'inserted';
}

/** The pinned skills with any repeat of an earlier chip's skill dropped, in document order. */
export function dedupePinnedSkills(pinned: PinnedSkill[]): PinnedSkill[] {
  const seen = new Set<string>();
  return pinned.filter(skill => {
    if (seen.has(skill.skillId)) return false;
    seen.add(skill.skillId);
    return true;
  });
}

/**
 * Apply the pinning rules to content about to be pasted next to the skills already `pinned`.
 *
 * Pasting a copied chip rebuilds it from its `skill:` link, which would skip the checks the
 * picker makes. A chip that repeats a skill, goes over the cap, or lands in an editor without
 * a skill picker becomes its name as plain text, so the pasted sentence still reads the same.
 */
export function limitPastedSkillChips({
  hasPicker,
  nodes,
  pinned,
}: {
  hasPicker: boolean;
  nodes: TElement[];
  pinned: PinnedSkill[];
}): { nodes: TElement[]; refusals: SkillPinRefusal[] } {
  const accepted = [...pinned];
  const refusals: SkillPinRefusal[] = [];

  function limitElement(element: TElement): TElement {
    return { ...element, children: element.children.map(limitNode) };
  }

  function limitNode(node: Descendant): Descendant {
    if (!getIsSkillElement(node)) return ElementApi.isElement(node) ? limitElement(node) : node;
    if (!hasPicker) return { text: node.value };

    const refusal = getSkillPinRefusal({ pinned: accepted, skillId: node.skillId });
    if (refusal) {
      refusals.push(refusal);
      return { text: node.value };
    }
    accepted.push(getPinnedSkillFromElement(node));
    return node;
  }

  return { nodes: nodes.map(limitElement), refusals };
}

function getPinnedSkillFromElement(node: TSkillElement): PinnedSkill {
  return {
    description: node.skillDescription,
    name: node.skillSlug,
    skillId: node.skillId,
    versionId: node.skillVersionId,
    ...(node.skillIsTestVersion && { isTestVersion: true }),
  };
}
